import {
  addNetToCurrentOrder,
  setDefaultDiameters,
  setDefaultGaps,
  setDefaultNets,
  setDefaultShapes,
  setLastUsedNets,
  removeLastUsedNets,
  updateCurrentOrderNet,
} from "Redux/features/net";
import { netStore } from "Redux/offline";
import { AppThunk } from "Redux/store";
import { isAxiosResponse } from "Services/index";
import { getDefaultNets, getLastUsedShapes } from "Services/net";
import {
  postOrderItem,
  updateOrderItem,
  getItemsCount,
} from "Services/order";
import { getMedia } from "Services/media";
import { NET_SUBCLASS_TYPE } from "Constants/net-constants";

let createNetQueue: Promise<any> = Promise.resolve();

const getPreviewImageId = (
  previewImage: any
): number | string | null => {
  if (!previewImage) {
    return null;
  }

  if (
    typeof previewImage === "number" ||
    typeof previewImage === "string"
  ) {
    return previewImage;
  }

  return previewImage.id ?? null;
};

const loadNetPreviewImage = async (netItem: any) => {
  const previewID = getPreviewImageId(
    netItem?.tool?.preview_image
  );

  if (!previewID) {
    throw new Error("לא התקבל מזהה לתמונה המקדימה של הרשת");
  }

  const mediaResponse = await getMedia(previewID);

  return {
    ...netItem,
    tool: {
      ...netItem.tool,
      preview_image: isAxiosResponse(mediaResponse)
        ? {
            ...mediaResponse.data,
          }
        : {
            id: previewID,
          },
    },
  };
};

// NOTE:
// row_number is 0 when creating a new item
// and greater than 0 when duplicating an item.
export const createNet =
  (
    net: any,
    orderItem: any,
    next = (_val: boolean) => {},
    rowNum = 0,
    orderId = 0
  ): AppThunk =>
  async (dispatch, getState) => {
    const createTask = async () => {
      try {
        const user = getState().user.userData;

        /*
         * Offline / unauthenticated order
         */
        if (!user) {
          const netData = {
            ...orderItem,
            tool: net,
          };

          const orderIt = await loadNetPreviewImage(netData);

          await netStore.setItem(orderIt);
          await dispatch(addNetToCurrentOrder(orderIt));

          next(true);
          return true;
        }

        /*
         * Logged-in order
         */
        let rowNumber = rowNum;

        if (rowNumber <= 0) {
          const countResult = await getItemsCount(
            NET_SUBCLASS_TYPE,
            orderId
          );

          if (!isAxiosResponse(countResult)) {
            throw new Error("קבלת מספר פריט הרשת הבא נכשלה");
          }

          if (
            countResult.data?.row_number === undefined ||
            countResult.data?.row_number === null
          ) {
            throw new Error("לא התקבל מספר פריט תקין עבור הרשת");
          }

          rowNumber = Number(countResult.data.row_number) + 1;
        }

        const payload = {
          ...orderItem,
          row_number: rowNumber,
          tool: net,
          user: user.id,
          ...(orderId > 0 ? { order: orderId } : {}),
        };

        const response = await postOrderItem(payload);

        if (!isAxiosResponse(response)) {
          throw new Error("יצירת פריט הרשת נכשלה");
        }

        if (!response.data) {
          throw new Error("לא התקבלו נתוני הרשת לאחר השמירה");
        }

        const orderIt = await loadNetPreviewImage(response.data);

        /*
         * Always add a successfully-created item to Redux.
         */
        await dispatch(addNetToCurrentOrder(orderIt));

        next(true);
        return true;
      } catch (error) {
        console.error("Failed to create net:", error);
        next(false);
        throw error;
      }
    };

    const queuedTask = createNetQueue.then(
      createTask,
      createTask
    );

    createNetQueue = queuedTask.then(
      () => undefined,
      () => undefined
    );

    return queuedTask;
  };

export const updateNet =
  (
    orderItemID: number,
    net: any,
    orderItem: any,
    next = (_val: boolean) => {}
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      const user = getState().user.userData;

      if (!user) {
        throw new Error("לא נמצא משתמש מחובר");
      }

      if (!orderItemID) {
        throw new Error("לא התקבל מזהה לפריט הרשת");
      }

      const response = await updateOrderItem(orderItemID, {
        ...orderItem,
        tool: net,
      });

      if (!isAxiosResponse(response)) {
        throw new Error("עדכון פריט הרשת נכשל");
      }

      if (!response.data) {
        throw new Error("לא התקבלו נתוני הרשת לאחר העדכון");
      }

      const orderIt = await loadNetPreviewImage(response.data);

      await dispatch(updateCurrentOrderNet(orderIt));

      next(true);
      return true;
    } catch (error) {
      console.error("Failed to update net:", error);
      next(false);
      throw error;
    }
  };

export const fetchDefaultsNets =
  (): AppThunk =>
  async (dispatch) => {
    const shapes = await getDefaultNets(2);

    if (!Array.isArray(shapes)) {
      throw new Error("טעינת הרשתות המוגדרות מראש נכשלה");
    }

    if (shapes.length === 0) {
      return true;
    }

    const diameters: number[] = [];
    const gaps: number[] = [];
    const xyLen: any[] = [];

    shapes.forEach((item: any) => {
      diameters.push(item.x_diameter.steel_diameter);
      gaps.push(item.x_gap);

      xyLen.push({
        val: {
          x: item.x_length,
          y: item.y_length,
        },
      });
    });

    const uniqueXYLen: any[] = Object.values(
      xyLen.reduce((accumulator: any, currentItem: any) => {
        const key =
          currentItem.val.x + "|" + currentItem.val.y;

        accumulator[key] = currentItem;

        return accumulator;
      }, {})
    );

    const standardShapes: any[] = [];

    uniqueXYLen.forEach((item: any) => {
      let netGaps: number[] = [];
      const netDiams: any[] = [];
      const steelTypes: any[] = [];
      let uniqueDiams: any[] = [];

      shapes.forEach((shape: any) => {
        if (
          item.val.x === shape.x_length &&
          item.val.y === shape.y_length
        ) {
          netDiams.push(shape.x_diameter);

          uniqueDiams = netDiams.reduce(
            (unique: any[], diameter: any) => {
              const alreadyExists = unique.some(
                (existingDiameter) =>
                  existingDiameter.steel_diameter ===
                  diameter.steel_diameter
              );

              if (!alreadyExists) {
                unique.push(diameter);
              }

              return unique;
            },
            []
          );

          uniqueDiams.sort(
            (a: any, b: any) =>
              a.steel_diameter - b.steel_diameter
          );

          netGaps.push(shape.y_gap);

          netGaps = Array.from(
            new Set(netGaps)
          ).sort((a: number, b: number) => a - b);

          steelTypes.push(shape.steel_type);
        }
      });

      standardShapes.push({
        lens: item.val,
        gaps: netGaps,
        dia: uniqueDiams,
        stTypes: steelTypes,
      });
    });

    await dispatch(setDefaultNets(shapes));
    await dispatch(setDefaultShapes(standardShapes));

    await dispatch(
      setDefaultDiameters(
        Array.from(new Set(diameters)).sort(
          (a: number, b: number) => a - b
        )
      )
    );

    await dispatch(
      setDefaultGaps(
        Array.from(new Set(gaps)).sort(
          (a: number, b: number) => a - b
        )
      )
    );

    return true;
  };

export const fetchLastUsedNets =
  (): AppThunk =>
  async (dispatch) => {
    const lastUsedShapes = await getLastUsedShapes();

    if (!Array.isArray(lastUsedShapes)) {
      throw new Error("טעינת הרשתות האחרונות נכשלה");
    }

    if (lastUsedShapes.length > 0) {
      await dispatch(setLastUsedNets(lastUsedShapes));
    } else {
      await dispatch(removeLastUsedNets());
    }

    return true;
  };